import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import { useToast } from '@/hooks/use-toast';
import {
  GetDealItemsDescriptionsDocument,
  type GetDealItemsDescriptionsQuery,
} from '@/lib/graphql/generated/graphql';
import { hasDescriptionFromText } from '@/lib/utils/has-description-from-text';
import { dealItemDescriptionsByActiveVersionIdState } from '@/states/deal-item-descriptions.state';
import { productsMapState } from '@/states/products.state';
import { useApolloClient } from '@apollo/client/react';
import { getDefaultStore, useSetAtom } from 'jotai';

/**
 * Indique qu’il faut appeler l’API de descriptions lazy.
 * On ne teste que `cached === null` : dès que l’atom a été rempli une fois, on ne refetch pas
 * à chaque clic (évite les requêtes en boucle si un id présent côté client manque encore dans la map).
 * Les invalidations (subscription, import, etc.) repassent l’atom à `null` dans `useDealItems` quand il faut recharger.
 */
export function dealItemDescriptionsNeedFetch(
  cached: Record<string, string | null> | null
): boolean {
  return cached === null;
}

/**
 * In-flight fetch, shared by every caller of the hook. Module-level on purpose:
 * the table and the VAT review dialog each hold their own hook instance, and a
 * per-instance ref would let them fire the same query twice at once.
 *
 * Carries its deal id so a pending request can only ever be handed back to a
 * caller asking for that same deal.
 */
let fetchInFlight: { dealId: string; promise: Promise<boolean> } | null = null;

/**
 * Loads the deal item descriptions on demand, once.
 *
 * Descriptions are excluded from the items list query to keep it light, so any
 * surface that needs the text (the description edit modal, the VAT review
 * dialog) fetches it through here — one in-flight request shared by all of them,
 * writing both the description cache and `productsMapState`.
 *
 * Resolves `true` when descriptions are available, `false` when the fetch failed
 * (the user has already been toasted).
 */
export const useEnsureDealItemDescriptions = (dealId: string) => {
  const { t } = useTranslation();
  const { toast } = useToast();
  const apolloClient = useApolloClient();
  const setProductsMap = useSetAtom(productsMapState);

  return useCallback(async (): Promise<boolean> => {
    if (!dealId) return true;

    const jStore = getDefaultStore();
    const cached = jStore.get(dealItemDescriptionsByActiveVersionIdState);
    if (!dealItemDescriptionsNeedFetch(cached)) return true;

    if (fetchInFlight?.dealId === dealId) return fetchInFlight.promise;

    const runFetch = async (): Promise<boolean> => {
      try {
        const { data } =
          await apolloClient.query<GetDealItemsDescriptionsQuery>({
            query: GetDealItemsDescriptionsDocument,
            variables: { dealId },
            fetchPolicy: 'network-only',
          });
        // A response for another deal must never land in the shared cache.
        if (data?.deal?.id !== dealId) return false;

        const items = data.deal.dealItemsWithVersions ?? [];
        const map: Record<string, string | null> = {};
        for (const item of items) {
          map[item.activeVersion.id] = item.activeVersion.description ?? null;
        }
        jStore.set(dealItemDescriptionsByActiveVersionIdState, map);
        setProductsMap((prev) => {
          const next = new Map(prev);
          for (const item of items) {
            const p = next.get(item.id);
            if (!p) continue;
            const descriptionText = item.activeVersion.description ?? null;
            next.set(item.id, {
              ...p,
              activeVersion: {
                ...p.activeVersion,
                description: descriptionText,
                hasDescription: hasDescriptionFromText(descriptionText),
              },
            });
          }
          return next;
        });
        return true;
      } catch (e) {
        console.error('GetDealItemsDescriptions failed', e);
        toast({
          title: t('general.error'),
          description: t('general.error_network_message'),
          variant: 'destructive',
        });
        return false;
      }
    };

    const fetchPromise = runFetch();
    fetchInFlight = { dealId, promise: fetchPromise };
    void fetchPromise.finally(() => {
      if (fetchInFlight?.promise === fetchPromise) fetchInFlight = null;
    });
    return fetchPromise;
  }, [apolloClient, dealId, setProductsMap, t, toast]);
};
